import dayjs from 'dayjs'
import { useContext, useEffect, useState } from 'react'

import { API } from '@/api'
import { Button, Loading, message, Modal, Radio, Trans } from '@/components'
import { AppContext } from '@/components/Layout'
import { ExchargeModal } from '@/pages/billing/components/RechargeModal.tsx'
import WarningInfoBlock from '@/pages/space-mgr/components/WarningInfoBlock.tsx'
import { useUserStore } from '@/stores/user'
import { formatPrice } from '@/utils/formatter'
import { cn } from '@/utils/style'

const BOX_PRICE_TYPE = {
  original: 1,
  first: 2,
  renew: 3,
  upgrade: 4,
  student: 5,
}
const PAYMENT_TYPE = {
  1: 30,
  default: 30,
}

const formatPackagePrice = function (prices: any[]) {
  if (!prices || !prices.length) return {}

  const originPrice = prices.find((p) => p.type === BOX_PRICE_TYPE.original)
  const firstPrice = prices.find((p) => p.type === BOX_PRICE_TYPE.first)
  const renewPrice = prices.find((p) => p.type === BOX_PRICE_TYPE.renew)
  const upgradePrice = prices.find((p) => p.type === BOX_PRICE_TYPE.upgrade)
  const studentPrice = prices.find((p) => p.type === BOX_PRICE_TYPE.student)
  const lastPrice = prices[prices.length - 1]

  return {
    originPrice,
    firstPrice,
    renewPrice,
    upgradePrice,
    studentPrice,
    lastPrice,
  }
}

interface ItemProps {
  data: {
    id: number
    placeholder?: boolean
    capacity: number
    currentPrice: number
    renewPrice: number
    originPrice: number
    period: string
    suggest?: string
  }
  currentId: number | null
  originalId: number | null
  onChange: (id: number) => void
  disabled: boolean
}

function Item({ data, currentId, originalId, onChange, disabled }: ItemProps) {
  if (data.placeholder === true) {
    return <div />
  }
  return (
    <div
      onClick={() => onChange(data.id)}
      className={cn(
        'w-full px-4 py-3 rounded-2xl gap-3 justify-start border border-gray-300 hover:bg-btn-secondary/5 hover:border-secondary focus:border-secondary focus:bg-btn-secondary/5 transition-colors duration-300 cursor-pointer',
        { 'opacity-50 cursor-not-allowed': disabled },
      )}
    >
      <div className="flex justify-between items-start">
        <div className=" font-bold text-[20px] leading-[28px] text-primary">
          {data.capacity / 1024}GB
        </div>
        <Radio checked={data.id === currentId} disabled={disabled} />
      </div>
      <div className="gap-2 flex">
        <div className=" t-sm text-primary">
          {formatPrice(data.id === originalId ? data.renewPrice : data.currentPrice)}
        </div>
        <div className=" t-sm text-tertiary">
          {data.period} <Trans i18nKey="space.expansionModal.days">Days</Trans>
        </div>
      </div>
    </div>
  )
}

export default function ExpansionModal(props: { open: boolean; onClose: () => void }) {
  const { open, onClose } = props
  const [items, setItems] = useState([] as any)
  const [selectedItem, setSelectedItem] = useState([] as any)
  const [originalId, setOriginalId] = useState<any>()
  const [currentId, setCurentId] = useState<any>()
  const [loading, setLoading] = useState(false)
  const [order, setOrder] = useState<any>(undefined)
  const userStore = useUserStore()
  const app = useContext(AppContext)
  const [exchargeModalOpen, setExchargeModalOpen] = useState(false)
  const [isNeedCharge, setIsNeedCharge] = useState(false)
  const [originalStorage, setOriginalStorage] = useState(0)
  const [selectedItemStorage, setSelectedItemStorage] = useState(0)
  const [autoRenewal, setAutoRenewal] = useState(false)

  const getButtonLabel = () => {
    if (isNeedCharge) {
      return <Trans i18nKey="space.chargeBtn">Recharge</Trans>
    }

    if (!order) {
      return <Trans i18nKey="space.expansionModal.checkoutBtn">Checkout</Trans>
    }

    if (originalStorage === selectedItemStorage) {
      return <Trans i18nKey="space.expansionModal.checkoutBtn">Checkout</Trans> // Renew
    }

    return originalStorage < selectedItemStorage ? (
      <Trans i18nKey="space.upgradePlanBtn">Upgrade Plan</Trans>
    ) : (
      <Trans i18nKey="space.downgradePlanBtn">Downgrade Plan</Trans>
    )
  }

  const handleButtonClick = () => {
    isNeedCharge ? setExchargeModalOpen(true) : onOK()
  }

  useEffect(() => {
    if (selectedItem) {
      setIsNeedCharge(
        Number(formatPrice(userStore.account.totalBonus.moneyCent, false, '')) <
          Number(formatPrice(selectedItem.currentPrice, false, '')),
      )
      setSelectedItemStorage(Number(selectedItem.capacity))
    }
  }, [selectedItem])

  useEffect(() => {
    if (open) {
      API.getOrders({ catalog: 1 }).then((response) => {
        const orders = response?.orders || []
        const order = orders[orders.length - 1]
        setOrder(order)
        setOriginalStorage(Number(order?.item?.desc?.storage?.capacityMB || 0))
        const extParams: any = {}
        if (order) {
          Object.assign(extParams, {
            from_order: true,
            from_order_id: order.id,
          })
        }
        API.getItems({
          page: 1,
          per_page: 1000,
          catalog: 1,
          domain: 0,
          ...extParams,
        })
          .then((response) => {
            return response.items
              .map((item: any) => {
                const {
                  originPrice,
                  lastPrice: currentPrice,
                  renewPrice,
                } = formatPackagePrice(item.prices || [])
                return {
                  id: item.id,
                  capacity: item.desc.storage.capacityMB,
                  currentPrice: currentPrice?.priceCent,
                  originPrice: originPrice?.priceCent,
                  renewPrice: renewPrice?.priceCent,
                  period: (PAYMENT_TYPE as any)[item.paymentType] || PAYMENT_TYPE.default,
                  disabled: item.desc.storage.capacityMB < (userStore.partVolume?.usage_mb || 0),
                }
              })
              .sort((prev: any, next: any) => prev.capacity - next.capacity)
          })
          .then((items) => {
            setItems(items)
            if (order) {
              const currentItem = items.find((item: any) => item.id === order.item.id)
              setOriginalId(currentItem?.id)
              setCurentId(currentItem?.id)
              setSelectedItem(currentItem)
            } else {
              setOriginalId(undefined)
              setCurentId(items?.[0]?.id)
              setSelectedItem(items?.[0])
            }
          })
          .catch((error) => {
            console.error(error)
          })
      })
    }
  }, [open])

  const onOK = () => {
    if (currentId) {
      setLoading(true)
      const extParams: any = {}
      if (order) {
        Object.assign(extParams, { modify: order.item.id !== currentId }) // upgrade or renew
      }
      API.purchase({
        item_id: currentId,
        auto_renewal: autoRenewal,
        ...extParams,
      })
        .then((res) => {
          if (res.code === 0) {
            message.success(order ? 'Plan modified successfully' : 'Plan purchased successfully')
            app.refresh()
            onClose()
          }
        })
        .catch((error) => {
          message.error(error.msg || 'Plan modification failed')
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  return (
    <>
      <Modal
        className="w-[800px]"
        open={open}
        onClose={() => {
          if (!loading) {
            onClose()
          }
        }}
      >
        <Modal.Header>
          <Trans i18nKey="space.expansionModal.title">Select Storage Plan</Trans>
        </Modal.Header>
        <Modal.Body>
          {items?.length > 0 ? (
            <>
              {selectedItemStorage !== originalStorage && (
                <WarningInfoBlock
                  _originalStorage={originalStorage}
                  _selectedItemStorage={selectedItemStorage}
                />
              )}
              <div className="max-h-[200px] flex-row flex-wrap justify-start gap-4 grid grid-cols-3 overflow-y-auto">
                {items.map((item: any) => {
                  return (
                    <Item
                      key={`item.${item.id}`}
                      data={item}
                      currentId={currentId}
                      originalId={originalId}
                      onChange={(id: any) => {
                        if (item.disabled) return
                        setCurentId(id)
                        setSelectedItem(item)
                      }}
                      disabled={item.disabled}
                    ></Item>
                  )
                })}
              </div>
            </>
          ) : (
            <div className="w-full flex items-center justify-center">
              <Loading />
            </div>
          )}

          <div className="mt-4">
            <h3 className="text-primary mb-2">
              <Trans i18nKey="space.expansionModal.autoRenewalTitle">Auto Renewal</Trans>
            </h3>

            <div className="flex items-center gap-2">
              <div className="flex-1 px-4 py-3 rounded-lg border border-neutral-400 cursor-pointer">
                <Radio
                  checked={!autoRenewal}
                  onChange={() => setAutoRenewal(false)}
                  className="t-sm text-primary"
                >
                  <Trans i18nKey="space.expansionModal.autoRenewalNoLabel">No</Trans>
                </Radio>
              </div>
              <div className="flex-1 px-4 py-3 rounded-lg border border-neutral-400 cursor-pointer">
                <Radio
                  checked={autoRenewal}
                  onChange={() => setAutoRenewal(true)}
                  className="t-sm text-primary"
                >
                  <Trans i18nKey="space.expansionModal.autoRenewalYesLabel">Yes</Trans>
                </Radio>
              </div>
            </div>
          </div>

          <h3 className="text-primary mt-4 mb-2">
            <Trans i18nKey="space.expansionModal.summary">Summary</Trans>
          </h3>

          <table className="table">
            <tbody>
              <tr className="h-[44px]">
                <td className="w-[120px] text-secondary t-sm !px-0 !py-3" align="left">
                  <Trans i18nKey="space.expansionModal.storage">Storage</Trans>
                </td>
                <td className="text-primary t-sm !px-0 !py-3">
                  {selectedItem?.capacity ? `${selectedItem?.capacity / 1024}GB` : '-'}
                </td>
              </tr>
              <tr className="h-[44px]">
                <td className="w-[120px] text-secondary t-sm !px-0 !py-3" align="left">
                  <Trans i18nKey="space.expansionModal.expire">Expires on</Trans>
                </td>
                <td className="text-primary t-sm !px-0 !py-3">
                  {!order
                    ? // purchase
                      dayjs().add(29, 'day').format('YYYY-MM-DD')
                    : originalId === selectedItem?.id
                      ? // renew
                        dayjs(order.deadlineTime.seconds * 1000)
                          .add(30, 'day')
                          .format('YYYY-MM-DD')
                      : // upgrade/downgrade
                        dayjs(order.deadlineTime.seconds * 1000).format('YYYY-MM-DD')}
                </td>
              </tr>
            </tbody>
          </table>
        </Modal.Body>
        <Modal.Footer className="justify-between">
          <div className="flex items-center space-x-2 ">
            <div className="h4 text-[#2bcaa5]">
              {(() => {
                if (order) {
                  if (originalId === selectedItem?.id) {
                    // renew
                    return formatPrice(selectedItem?.renewPrice)
                  } else {
                    // upgrade/downgrade
                    return formatPrice(selectedItem?.currentPrice)
                  }
                } else {
                  // purchase
                  return formatPrice(selectedItem?.currentPrice)
                }
              })()}
            </div>
            <div className="rounded-full text-secondary py-2 px-3 bg-[#F5F7FA] t-x-sb">
              <Trans i18nKey="space.expansionModal.myBalance">My Balance</Trans>
              <span className="text-primary ml-2">
                {formatPrice(userStore.account.totalBonus.moneyCent)}
              </span>
            </div>
          </div>
          <div className="space-x-4">
            <Button
              type="outline"
              onClick={() => {
                if (!loading) {
                  onClose()
                }
              }}
            >
              <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
            </Button>

            <Button
              type="secondary"
              disabled={typeof currentId === 'undefined' || loading}
              onClick={handleButtonClick}
            >
              {getButtonLabel()}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {exchargeModalOpen && (
        <ExchargeModal
          onOk={() => {
            setExchargeModalOpen(false)
            userStore.getUserInfo()
          }}
          onClose={() => {
            setExchargeModalOpen(false)
          }}
        />
      )}
    </>
  )
}
